import Grid from '@material-ui/core/Grid';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import { default as React } from 'react';
import { ENTITLEMENTS } from '../application-core/constants';
import ActionText from '../core/ActionText';
import BasicReadField from '../core/BasicReadField';
import Errored from '../core/Errored';
import Notification from '../core/Notification';
import TitledPanel from '../core/TitledPanel';
import { ProjectConsumer } from '../core/providers/ProjectProvider';
import { globalStyles as styles } from '../core/style/gstheme';
import { STATUS_ACCEPTED, STATUS_REJECTED } from '../core/utils/constants';
import { checkResourceEntitlements, displayDate } from '../core/utils/utils';
import withGS from '../core/withGShoc';
import GS from '../grafsync/grafsync';
import AssociatedProjectDisplay from '../application-core/AssociatedProjectDisplay';
import WorkflowResourceManager from '../core/workflow/WorkflowResourceManager';
import TypeDisplay from '../vocabulary/taxonomies/TypeDisplay';
import FindOntologyRelatedDataDualModePassthroughDialog from './FindOntologyRelatedDataDualModePassthroughDialog';
import DataLabels from './DataLabels';
import DataLabelDialog from './DataLabelDialog';
import CommonIriDisplay from '../application-core/CommonIriDisplay';
import ReificationContainer from '../application-core/ReificationContainer';

const APPROVED = STATUS_ACCEPTED;
const REJECTED = STATUS_REJECTED;

class ResourceDetail extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            isErrored: undefined,
            reificationSubject: undefined,
            reificationPredicate: undefined,
            reificationObject: undefined
        };
    }

    onNotSure = () => this.setState({ areYouSure: false, confirmPropertyRemoval: false });

    addNewTypedLiteral = (property, propertyGraphIRI) => (value, datatype = null) => {
        GS.addProperty(this.props.id, this.props.graph, property, value, null, datatype, propertyGraphIRI);
    };

    addNewLiteral = (property, propertyGraphIRI) => (language, text) => {
        GS.addProperty(this.props.id, this.props.graph, property, text, language, null, propertyGraphIRI);
    };

    onActualUpdate = (property, lang, index, propertyGraphIRI) => (value) => {
        this.props.onUpdate(property, value, lang, index, propertyGraphIRI);
    };

    addNewLabel = (property) => async (language, label) => {
        if (property === "label") {
            if (this.props.data.label && this.props.data.label[language]) {
                this.setState({
                    warning: "Another user had just added a  label in the same language that you tried to add. Your edit will not be applied."
                });
                return;
            }
        }
        GS.addProperty(this.props.id, this.props.graph, property, label, language);
    };

    removeLabel = (property, language, value) => GS.deleteProperty(this.props.id, this.props.graph, property, value, language);

    onDelete = (property, lang, datatype, propertyGraphIRI) => (value) =>
        this.props.onDelete(property, value, lang, datatype, propertyGraphIRI);

    onAddClass = async (value) => this.props.onAdd("type", value);

    includeExistingResource = (property, propertyGraphIRI) => async (newIRI) => {
        const { graph, id } = this.props;
        // We load the resource we are about to use to make sure rendering works okay
        // But if it's external it won't load and that's okay
        GS.loadData([newIRI], {}, graph, true).then(() => {
            GS.addProperty(id, graph, property, newIRI, null, null, propertyGraphIRI);
        });
    };

    onShowReification = (showReification, reificationSubject, reificationPredicate, reificationObject) => {
        this.setState({
            showReification,
            reificationSubject,
            reificationPredicate,
            reificationObject
        });
    };

    render() {

        const { isErrored } = this.state;

        if (isErrored) return <Errored data={isErrored} />;

        const { data, id, datasetIRI, propertyTemplates, defaultLang, showOntologyProjects, linkedData, useWorkflow,
            graph, viewLang, associatedProject, project, entitlements, projectUsers, annotate, useSlashIRI } = this.props;
        const { showReification, reificationSubject, reificationPredicate, reificationObject } = this.state;

        if (data._notAvailable) return null;
        if (data.pendingReplacement) return <ActionText offset="100px">This resource has been replaced. Select again to view new data.</ActionText>;

        const isReadOnly = !datasetIRI || checkResourceEntitlements(data, ENTITLEMENTS.EDIT_DATASET)
            || !data.inDataset || !data.inDataset.includes(datasetIRI) || (data.deprecated === true)
            || (useWorkflow && "status" in data && (data.status[0] === APPROVED || data.status[0] === REJECTED));

        let resourceClasses = Array.isArray(data.type) ? data.type : [data.type];
        // Some of the classes may be aliased so convert to IRIs
        resourceClasses = resourceClasses.map(rc => GS.getIRIfromAlias(rc) || rc);

        return (
            <div>
                <ProjectConsumer>
                    {({ id: projectIRI, projectLang, hasComponent, ontologyProjectAssociation }) => (
                        <form aria-label="Resource detail" autoComplete="off">
                            <Grid container spacing={1}>
                                {useWorkflow &&
                                    <Grid item xs={12}>
                                        <WorkflowResourceManager
                                            disabled={(!data.inDataset || data.inDataset.length === 0)
                                                || !(data.inDataset.includes(datasetIRI))
                                                || data.deprecated || data.pendingDeletion}
                                            status={data.status}
                                            id={id}
                                            key={id}
                                            resourceClass="https://grafsync.graphifi.com/schema/gs/Resource"
                                            componentIRI={datasetIRI}
                                            entitlements={entitlements}
                                            defaultLang={defaultLang}
                                            assignedUser={data.assignedUser}
                                            assignedNote={data.assignedNote}
                                            rejectedNote={data.rejectedNote}
                                            projectUsers={projectUsers}
                                            project={project}
                                            graph={graph} />
                                    </Grid>
                                }
                                <Grid item xs={12}>
                                    {data.pendingDeletion &&
                                        <Notification error={true} text="This resource is pending deletion." dataTest="pending-deletion" />
                                    }
                                    {data.deprecated &&
                                        <Notification text="This resource is deprecated." dataTest="deprecated" />
                                    }
                                    {(!data.inDataset || data.inDataset.length === 0) &&
                                        <Notification text="This resource is not in any dataset." dataTest="not-in-dataset" />
                                    }
                                </Grid>
                                <Grid item md={12} lg={6}>
                                    <TitledPanel
                                        height="full"
                                        dataTest="label"
                                        title="Label"
                                        actions=
                                        {!isReadOnly &&
                                            projectLang.filter(lang =>
                                                data.label ? Object.keys(data.label).includes(lang) : false).length !== projectLang.length ?
                                            <DataLabelDialog
                                                langs={projectLang}
                                                onePerLang={true}
                                                existingValues={data.label}
                                                defaultLang={defaultLang}
                                                datasetIRI={datasetIRI}
                                                viewLang={viewLang}
                                                graph={graph}
                                                title="Add Label"
                                                name="Label"
                                                placeholder="Enter label text"
                                                existingLangs={"label" in data ? Object.keys(data.label) : []}
                                                onAddLanguage={this.addNewLabel("label")} />
                                            :
                                            null
                                        }>
                                        <DataLabels
                                            labels={data.label}
                                            name="label"
                                            datasetIRI={datasetIRI}
                                            existingValues={data.label}
                                            canDeleteIfDefault={false}
                                            projectIRI={projectIRI}
                                            id={id}
                                            onRemoveLabel={this.removeLabel}
                                            defaultLang={defaultLang}
                                            isReadOnly={isReadOnly}
                                            onUpdate={this.onActualUpdate} />
                                    </TitledPanel>
                                </Grid>
                                <Grid item md={12} lg={6}>
                                    <TypeDisplay
                                        offerInheritance={false}
                                        graph={graph}
                                        classIRIs={GS.getClassIRIs(id, graph)}
                                        ontologyProjectAssociation={ontologyProjectAssociation}
                                        isReadOnly={isReadOnly}
                                        defaultLang={defaultLang}
                                        viewLang={viewLang}
                                        onAddClass={this.onAddClass}
                                        id={id}
                                        values={data.type} />
                                </Grid>
                                {data.inDataset &&
                                    <Grid item md={12} lg={6}>
                                        <TitledPanel dataTest="inDataset" height="full" title="In Dataset">
                                            {[...data.inDataset].sort().map(sIRI =>
                                                <CommonIriDisplay
                                                    key={sIRI}
                                                    id={sIRI}
                                                    component={hasComponent}
                                                    viewLang={viewLang}
                                                    defaultLang={defaultLang}
                                                    projectIRI={project}
                                                    property="inDataset"
                                                    isReadOnly={isReadOnly}
                                                    graph={graph}
                                                    source={id} />
                                            )}
                                        </TitledPanel>
                                    </Grid>
                                }
                                {data.iri &&
                                    <Grid item md={12} lg={6}>
                                        <TitledPanel dataTest="iri-history" title="IRI History">
                                            {data.iri.reverse().map((iri, index) =>
                                                index > 0 ?
                                                    <BasicReadField ariaLabel="IRI history" key={index} dataTest="iri-history-item" margin="dense" text={iri} fullWidth />
                                                    :
                                                    null
                                            )}
                                            <BasicReadField dataTest="iri-history-item" margin="dense" text={id} fullWidth />
                                        </TitledPanel>
                                    </Grid>
                                }
                                {(data.created) &&
                                    <Grid item md={12} lg={6}>
                                        <TitledPanel dataTest="dateCreated" title="Date Created">
                                            <BasicReadField dataTest="created-date" ariaLabel="Date created" margin="dense" text={displayDate(data.created)} />
                                        </TitledPanel>
                                    </Grid>
                                }
                                {showOntologyProjects && ontologyProjectAssociation &&
                                    [...ontologyProjectAssociation].sort().map((opa) =>
                                        <AssociatedProjectDisplay
                                            resourceData={data}
                                            dialogComponent={FindOntologyRelatedDataDualModePassthroughDialog}
                                            graph={graph}
                                            reify={annotate}
                                            onReify={this.onShowReification}
                                            projectIRI={projectIRI}
                                            schemeIRI={datasetIRI}
                                            statements={data.isSubjectOfStatement}
                                            useWorkflow={useWorkflow}
                                            propertyTemplates={propertyTemplates}
                                            projectType="DataProject"
                                            isReadOnly={isReadOnly}
                                            viewLang={viewLang}
                                            projectLang={projectLang}
                                            linkedData={linkedData}
                                            associatedTaxonomyProject={associatedProject}
                                            addNewLiteral={this.addNewLiteral}
                                            addNewTypedLiteral={this.addNewTypedLiteral}
                                            includeExistingResource={this.includeExistingResource}
                                            components={hasComponent}
                                            resourceClasses={resourceClasses}
                                            onDelete={this.onDelete}
                                            onUpdate={this.onActualUpdate}
                                            key={opa}
                                            id={opa}
                                            defaultLang={defaultLang} />
                                    )
                                }
                            </Grid>
                            {showReification &&
                                <ReificationContainer
                                    resourceData={data}
                                    subject={reificationSubject}
                                    property={reificationPredicate}
                                    dialogComponent={FindOntologyRelatedDataDualModePassthroughDialog}
                                    value={reificationObject}
                                    useSlashIRI={useSlashIRI}
                                    statements={data.isSubjectOfStatement}
                                    ontologyProjectAssociation={ontologyProjectAssociation}
                                    graph={graph}
                                    isReadOnly={isReadOnly}
                                    showDialog={this.onShowReification}
                                    projectIRI={projectIRI}
                                    baseIRI={datasetIRI}
                                    componentIRI={datasetIRI}
                                    useWorkflow={useWorkflow}
                                    propertyTemplates={propertyTemplates}
                                    viewLang={viewLang}
                                    projectLang={projectLang}
                                    projectType="DataProject"
                                    linkedData={linkedData}
                                    associatedTaxonomyProject={associatedProject}
                                    components={hasComponent}
                                    defaultLang={defaultLang} />}
                        </form>
                    )}
                </ProjectConsumer>
                <div data-test="info-panel-bottom" >&#160;</div>
            </div >
        );
    }
}

ResourceDetail.defaultProps = {
    useWorkflow: false
};

ResourceDetail.propTypes = {
    data: PropTypes.object.isRequired,
    id: PropTypes.string.isRequired,
    onAdd: PropTypes.func,
    onChange: PropTypes.func,
    onUpdate: PropTypes.func,
    project: PropTypes.string.isRequired,
    projectUsers: PropTypes.array,
    entitlements: PropTypes.array.isRequired,
    showOntologyProjects: PropTypes.bool.isRequired,
    defaultLang: PropTypes.string.isRequired,
    useWorkflow: PropTypes.bool,
    viewLang: PropTypes.string,
    propertyTemplates: PropTypes.array,
    graph: PropTypes.string.isRequired,
    datasetIRI: PropTypes.string,
    useSlashIRI: PropTypes.bool,
    annotate: PropTypes.bool,
    useUuidIRI: PropTypes.bool,
    linkedData: PropTypes.array,
    associatedProject: PropTypes.array,
    onDelete: PropTypes.func.isRequired,
    onDeleteResource: PropTypes.func.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(withGS(ResourceDetail, ["ALL"], null, {}, true));